import { useState } from 'react'
import { useSnackbar } from '@shared/ui/notification-context'
import { useTranslation } from 'react-i18next'
import { updateDashboardProfile, type DashboardUserProfile } from '@services/firebase'
import { Card, CardAction, CardContent, CardHeader, CardTitle, Field, FieldLabel, LmsButton, LmsInput } from '@shared/ui'
import '../profile-layout.css'

export function AccountSettingsCard({
  onSaved,
  profile,
  shell = 'card',
}: {
  readonly onSaved: (patch: Partial<DashboardUserProfile>) => void
  readonly profile: DashboardUserProfile
  readonly shell?: 'card' | 'plain'
}) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('form_account')
  const [form, setForm] = useState({
    firstname: profile.firstname ?? '',
    lastname: profile.lastname ?? '',
    middlename: profile.middlename ?? '',
    phone: profile.phone ?? '',
    username: profile.username ?? '',
  })
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    try {
      setIsSaving(true)
      await updateDashboardProfile(form)
      onSaved(form)
      enqueueSnackbar(t('Updated successfully', { defaultValue: 'Updated successfully' }), {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar(t('Updated failed', { defaultValue: 'Updated failed' }), { variant: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const content = (
    <div className="profileFormRows">
      <Field className="profileFormRow">
        <FieldLabel htmlFor="account-firstname">{t('First name')}</FieldLabel>
        <LmsInput
          id="account-firstname"
          onChange={(event) => {
            setForm((previous) => ({ ...previous, firstname: event.target.value }))
          }}
          value={form.firstname}
        />
      </Field>
      <Field className="profileFormRow">
        <FieldLabel htmlFor="account-middlename">{t('Middle name')}</FieldLabel>
        <LmsInput
          id="account-middlename"
          onChange={(event) => {
            setForm((previous) => ({ ...previous, middlename: event.target.value }))
          }}
          value={form.middlename}
        />
      </Field>
      <Field className="profileFormRow">
        <FieldLabel htmlFor="account-lastname">{t('Last name')}</FieldLabel>
        <LmsInput
          id="account-lastname"
          onChange={(event) => {
            setForm((previous) => ({ ...previous, lastname: event.target.value }))
          }}
          value={form.lastname}
        />
      </Field>
      <Field className="profileFormRow">
        <FieldLabel htmlFor="account-username">{t('Username', { defaultValue: 'Username' })}</FieldLabel>
        <LmsInput
          id="account-username"
          onChange={(event) => {
            setForm((previous) => ({ ...previous, username: event.target.value }))
          }}
          value={form.username}
        />
      </Field>
      <Field className="profileFormRow">
        <FieldLabel htmlFor="account-phone">{t('Number phone')}</FieldLabel>
        <LmsInput
          id="account-phone"
          onChange={(event) => {
            setForm((previous) => ({ ...previous, phone: event.target.value }))
          }}
          value={form.phone}
        />
      </Field>
      <Field className="profileFormRow">
        <FieldLabel htmlFor="account-email">{t('Email', { defaultValue: 'Email' })}</FieldLabel>
        <LmsInput disabled id="account-email" readOnly value={profile.email ?? ''} />
      </Field>
    </div>
  )

  const actions = (
    <LmsButton
      className="min-h-11 px-6"
      isLoading={isSaving}
      loadingLabel={t('Submitting', { defaultValue: 'Saving' })}
      onClick={() => void handleSave()}
      variant="primary"
    >
      {t('Save', { defaultValue: 'Save' })}
    </LmsButton>
  )

  if (shell === 'plain') {
    return (
      <div className="profileCardStack">
        <div className="profileHeaderRow">
          <h2 className="profileCardTitle">{t('Account')}</h2>
          <div className="profileHeaderAction">{actions}</div>
        </div>

        {content}

        <div className="profileActionRow profileMobileSubmit">{actions}</div>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Account')}</CardTitle>
        <CardAction>{actions}</CardAction>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}
